import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Paper, Typography,
} from '@mui/material';
import { useDbtReadiness, useResetDbtNodes } from 'api/warehouse';
import { extractApiError } from 'api/errors';
import ErrorBoundary from 'components/common/ErrorBoundary';
import useRole from 'hooks/useRole';
import { useTranslation } from 'react-i18next';
import ReadinessGraph from './ReadinessGraph';

/** The preparation graph and its guarded rebuild action; history is a child. */
export default function ReadinessTab({ projectId, children }) {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const { data, isLoading, error } = useDbtReadiness(projectId);
  const reset = useResetDbtNodes(projectId);
  const [resetTarget, setResetTarget] = useState(null);

  useEffect(() => { setResetTarget(null); }, [projectId]);
  useEffect(() => { if (!isAdmin) setResetTarget(null); }, [isAdmin]);

  const requestReset = (target) => {
    if (!isAdmin) return;
    reset.reset();
    setResetTarget(target);
  };
  const confirmReset = () => {
    if (!isAdmin || !resetTarget) return;
    reset.mutate(
      { node: resetTarget.node.unique_id, descendants: Boolean(resetTarget.withDescendants) },
      { onSuccess: () => setResetTarget(null) },
    );
  };

  if (isLoading) return <Box><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>{children}</Box>;
  if (error) return <Box><Alert severity="error">{extractApiError(error, t('data.failedToLoadReadiness'))}</Alert>{children}</Box>;
  if (!data) return children || null;

  const nodes = data.nodes || [];
  const physicalLeaves = [];
  const byName = new Map();
  (data.leaves || []).forEach((leaf) => {
    const seen = byName.get(leaf.name);
    if (seen) {
      seen.readers += leaf.readers;
      seen.optional = seen.optional && leaf.optional;
      seen.declarations += 1;
    } else {
      const merged = { ...leaf, declarations: 1 };
      byName.set(leaf.name, merged);
      physicalLeaves.push(merged);
    }
  });
  const reason = data.planner?.reason;
  const reasonText = {
    planner_off: t('data.theReadinessPlannerIsNotEnabledForYourOrganizationEveryLoad'),
    project_inactive: t('data.theProjectIsDisabled'),
    model_selector: t('data.theProjectRunsWithAFixedModelSelectorSoThePlanner'),
    no_map: t('data.noLineageMapYetItArrivesWithTheFirstRunOn'),
    no_landings: t('data.noLandingsOnRecordForThisWarehouseYetSoThePost'),
  }[reason];

  return (
    <Box>
      {reasonText && reason !== 'no_map' && <Alert severity={reason === 'planner_off' ? 'info' : 'warning'} sx={{ mb: 2 }}>{reasonText}</Alert>}
      {nodes.length ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>{t('data.dependencyGraph')}</Typography>
          <ErrorBoundary name="The dependency graph">
            <ReadinessGraph nodes={nodes} leaves={physicalLeaves} onReset={isAdmin ? requestReset : undefined} />
          </ErrorBoundary>
        </Box>
      ) : (
        <Paper sx={{ p: 4, mb: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">{t('data.workspacePreparation.graphEmpty')}</Typography>
        </Paper>
      )}
      {children}
      <Dialog open={isAdmin && Boolean(resetTarget)} onClose={() => setResetTarget(null)} maxWidth="xs" fullWidth>
        {isAdmin && resetTarget && <>
          <DialogTitle sx={{ fontFamily: 'monospace', fontSize: 16 }}>
            {t('data.reset')}{' '}{resetTarget.node.name}{resetTarget.withDescendants && resetTarget.descendants.length > 0 ? t('data.andValueBelow', { value1: resetTarget.descendants.length }) : ''}?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('data.thePlannerForgetsItWasEverBuiltEveryInputThatHas')}</Typography>
            {resetTarget.withDescendants && resetTarget.descendants.length > 0 && (
              <Typography variant="body2" sx={{ mb: 1.5 }}>{t('data.alsoReset')}{' '}<Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{resetTarget.descendants.join(', ')}</Box></Typography>
            )}
            {reasonText && <Alert severity="warning" sx={{ mb: 1 }}>{t('data.thePlannerDoesNotApplyToThisProjectRightNow')}{reasonText}{t('data.theResetIsRecordedTheNextWholeProjectBuildCoversIt')}</Alert>}
            {reset.error && <Alert severity="error">{extractApiError(reset.error, t('data.theResetFailed'))}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetTarget(null)} color="inherit">{t('data.cancel')}</Button>
            <Button variant="contained" disabled={reset.isPending} onClick={confirmReset}>{reset.isPending ? t('data.resetting') : t('data.reset')}</Button>
          </DialogActions>
        </>}
      </Dialog>
    </Box>
  );
}
